import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FileText } from 'lucide-react';
import TipTapEditor from './TipTapEditor';
import { Section, DiffEntry } from '@/types/sections';
import { updateAnalysisIaReport, saveDiffToAnalysis } from '@/config/api';
import { useToast } from '@/hooks/use-toast';
import { DiffViewer } from './DiffViewer';
import { useIsMobile } from '@/hooks/use-mobile';
import { debugLog } from '@/config/debug';

interface ReportSectionProps {
  section: Section;
  content: string;
  onContentChange: (sectionKey: string, content: string) => void;
  onDiffChange: (sectionKey: string, diff: DiffEntry) => void;
  onEditingChange?: (sectionKey: string, isEditing: boolean) => void;
  analysisId: number;
  isListening?: boolean;
  existingDiffs?: DiffEntry[];
}

const ReportSection: React.FC<ReportSectionProps> = ({
  section,
  content,
  onContentChange,
  onDiffChange,
  onEditingChange,
  analysisId,
  isListening = false,
  existingDiffs = []
}) => {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showDiff, setShowDiff] = useState(false);
  const [sectionDiffs, setSectionDiffs] = useState<DiffEntry[]>(existingDiffs);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const lastAutoContent = useRef<string>(content);

  // Cargar diffs existentes cuando cambie existingDiffs
  useEffect(() => {
    setSectionDiffs(existingDiffs);
  }, [existingDiffs]);

  // Log usando la configuración de debug
  debugLog('HOOK', `🔄 REPORT: Renderizando sección: ${section.key} con diffs:`, existingDiffs);

  const debouncedSave = useCallback(async (newContent: string) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        await updateAnalysisIaReport(analysisId, section.key, newContent);
        setSaveStatus('saved');

        // Reset to idle after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        console.error('Error saving content:', error);
        setSaveStatus('error');
        toast({
          title: 'Error al guardar',
          description: 'No se pudo guardar el contenido. Inténtalo de nuevo.',
          variant: 'destructive'
        });

        // Reset to idle after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }, 2000);
  }, [analysisId, section.key, toast]);

  const handleContentChange = useCallback((newContent: string) => {
    onContentChange(section.key, newContent);
    // Always save changes when content changes
    debouncedSave(newContent);
  }, [section.key, onContentChange, debouncedSave]);

  const handleDiffChange = useCallback(async (diff: DiffEntry) => {
    debugLog('HOOK', '📝 SECTION: Recibiendo diff del usuario para sección:', section.key);
    debugLog('HOOK', '📝 SECTION: Contenido del diff recibido:', diff);
    
    // Validar que el diff tenga la estructura correcta
    if (!diff || !diff.timestamp || !diff.editor || !diff.diff) {
      console.error('❌ SECTION: Diff inválido recibido:', diff);
      toast({
        title: 'Error',
        description: 'El formato del diff es inválido',
        variant: 'destructive'
      });
      return;
    }
    
    // Agregar a la lista local
    setSectionDiffs(prev => {
      const newDiffs = [...prev, diff];
      debugLog('HOOK', '📝 SECTION: Diffs actualizados localmente:', newDiffs);
      return newDiffs;
    });
    
    // Notificar al componente padre
    onDiffChange(section.key, diff);
    
    // Guardar en la API
    try {
      debugLog('HOOK', '📝 SECTION: Enviando diff a la API:', diff);
      const result = await saveDiffToAnalysis(analysisId, section.key, diff);
      debugLog('HOOK', '✅ SECTION: Respuesta de la API:', result);
    } catch (error) {
      console.error('❌ SECTION: Error guardando diff:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el historial de cambios',
        variant: 'destructive'
      });
    }
  }, [section.key, onDiffChange, analysisId, toast]);

  const handleFocus = useCallback(() => {
    if (onEditingChange) {
      onEditingChange(section.key, true);
    }
  }, [section.key, onEditingChange]);

  const handleBlur = useCallback(() => {
    if (onEditingChange) {
      // Small delay to avoid rapid focus/blur cycles
      setTimeout(() => {
        onEditingChange(section.key, false);
      }, 100);
    }
  }, [section.key, onEditingChange]);

  // Detectar cuando el contenido cambia automáticamente (IA)
  useEffect(() => {
    if (content !== lastAutoContent.current) {
      debugLog('HOOK', '📝 SECTION: Contenido actualizado automáticamente por IA:', section.key);
      lastAutoContent.current = content;
    }
  }, [content, section.key]);

  // Show placeholder if content is empty or shows "registrar"
  const isEmpty = !content || content.includes('Registering the consultation') || content.includes('registrar');
  const displayContent = isEmpty ? `<p>${section.description || `Contenido para ${section.title}...`}</p>` : content;

  const getSaveStatusText = () => {
    switch (saveStatus) {
      case 'saving':
        return 'Guardando...';
      case 'saved':
        return 'Guardado';
      case 'error':
        return 'Error al guardar';
      default:
        return '';
    }
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4 px-[15px]">
        <h2 className="text-xl font-semibold" style={{ color: '#2A4276' }}>
          {section.title}
        </h2>
        
        {/* Solo mostrar el botón de diffs en desktop */}
        {!isMobile && sectionDiffs.length > 0 && (
          <button
            onClick={() => setShowDiff(!showDiff)}
            className="text-xs text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors border border-gray-200 flex items-center gap-2 whitespace-nowrap min-w-fit"
          >
            <FileText className="w-3 h-3 flex-shrink-0" />
            <span>Ver cambios ({sectionDiffs.length})</span>
          </button>
        )}
      </div>

      {/* Solo mostrar diffs en desktop */}
      {!isMobile && showDiff && sectionDiffs.length > 0 && (
        <div className="mb-4 px-[15px]">
          <DiffViewer diffs={sectionDiffs} />
        </div>
      )}
      
      <div className="relative">
        {saveStatus !== 'idle' && (
          <div className={`save-indicator ${saveStatus}`}>
            {getSaveStatusText()}
          </div>
        )}
        
        <TipTapEditor 
          initialValue={displayContent} 
          onChange={handleContentChange} 
          onDiffChange={handleDiffChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          sectionKey={section.key} 
          isListening={isListening} 
        />
      </div>
    </section>
  );
};

export default ReportSection;
